import { configureStore, combineReducers } from '@reduxjs/toolkit';
import {
  persistStore,
  persistReducer,
  createTransform,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER
} from 'redux-persist';
import storageSession from 'redux-persist/lib/storage/session';
import visitorSlice from './slices/visitorSlice';
import appSlice from './slices/appSlice';
import authSlice from './slices/authSlice';

// Strip isLoading before writing to sessionStorage and after rehydrating.
// Prevents the full-screen spinner from re-appearing on refresh if a request
// was in-flight when the page was reloaded.
const appTransform = createTransform(
  (inboundState: any) => ({ ...inboundState, isLoading: false }),
  (outboundState: any) => ({ ...outboundState, isLoading: false }),
  { whitelist: ['app'] }
);

const persistConfig = {
  key: 'root',
  storage: storageSession,
  transforms: [appTransform],
};

const rootReducer = combineReducers({
  visitor: visitorSlice,
  app: appSlice,
  auth: authSlice,
});

// redux-persist's type definitions predate RTK v2's 3-param Reducer generic.
// The cast is safe — the runtime behaviour is unchanged.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const persistedReducer = persistReducer(persistConfig, rootReducer as any);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export const persistor = persistStore(store);

// Derived from individual slice reducers (not store.getState / persistedReducer)
// to avoid CombinedState's | undefined widening on each slice key.
export type RootState = {
  visitor: ReturnType<typeof visitorSlice>;
  app: ReturnType<typeof appSlice>;
  auth: ReturnType<typeof authSlice>;
};
export type AppDispatch = typeof store.dispatch;